import { z } from 'zod';
import {
  CASAS_DINHEIRO,
  CASAS_PERCENTUAL,
  compararQuantidade,
  desescalar,
  dividirArredondando,
  escalar,
  multiplicarQuantidadePorValor,
  somarQuantidade,
} from './aritmetica.ts';
import {
  dataDoFatoSchema,
  dataHoraSchema,
  dataSchema,
  dinheiroSchema,
  filtroAtivoSchema,
  paginacaoSchema,
  precoUnitarioSchema,
  quantidadeSchema,
  respostaListaSchema,
  uuidSchema,
} from './comum.ts';
import {
  impostosDestacadosEntradaSchema,
  impostosDestacadosSchema,
  impostosZerados,
  type ImpostosDestacados,
  type ImpostosDestacadosEntrada,
} from './impostos.ts';

/**
 * Cotação de preço (`.spec/010`, "cotação de preço").
 *
 * É onde o **preço entra no sistema**. A solicitação de compra diz o que a obra
 * precisa e não tem valor nenhum; a cotação pega **um insumo** e uma quantidade
 * e pergunta a vários fornecedores quanto custa. O pedido de compra nasce
 * depois, da proposta escolhida.
 *
 * A forma é a mesma da solicitação — mestre e filhos num payload só, como na
 * condição de pagamento —, mas **três coisas mudam**, e as três são de negócio:
 *
 * - **Um insumo por cotação, não uma lista.** Cotar cimento e cabo é cotar duas
 *   coisas: os fornecedores são outros, o prazo é outro, e a proposta de quem
 *   cobre um item e não o outro não se compara com nada. O filho aqui é a
 *   **proposta do fornecedor**, e é ela que se repete.
 * - **A cotação nasce sem proposta.** É o oposto da solicitação, que exige um
 *   item: abrir a cotação é o ato de *perguntar*, e a resposta chega dias
 *   depois — é para isso que existe o prazo de resposta. Exigir uma proposta na
 *   inclusão obrigaria a inventar a primeira para poder gravar a pergunta.
 * - **Tem `ativo`, e não situação.** `.spec/010` pede assim, e é coerente: a
 *   cotação não tem saldo a atender nem a baixar. O que ela tem é vigência — o
 *   preço de março não serve para comprar em agosto —, e quem a encerra é o
 *   `ativo`, como em qualquer cadastro.
 *
 * O que **não** muda é a trava: assim como a solicitação não se altera depois
 * que um pedido a cobriu, a cotação não se altera depois que uma proposta virou
 * pedido. `quantidadePedido` é a coluna equivalente a `quantidadeAtendida`, e
 * também não é campo do usuário.
 */

/* -------------------------------------------------------------------------- */
/* Códigos e rótulos                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Quem paga o frete (`.spec/010`).
 *
 * `SEM` é um código, e não a ausência de escolha: "não há frete" — retirada no
 * balcão, serviço prestado na obra — é uma informação, e deixá-la como `null`
 * obrigaria toda comparação de propostas a tratar dois jeitos de dizer que o
 * transporte não pesa no preço.
 *
 * A modalidade decide **se há valor de frete a informar**, e só no `CIF` há:
 * quem entrega é o fornecedor, e ele cobra por isso — muitas vezes destacado na
 * proposta, separado do preço da mercadoria. Esse valor entra no total, porque
 * sai do mesmo pagamento.
 *
 * No `FOB` o transporte é contratado pela construtora, com outro fornecedor e
 * outra nota: a cotação não conhece o valor, e um campo aberto ali produziria um
 * número chutado somado ao total como se fosse fato. No `SEM` não há transporte
 * a pagar. Nos dois casos o valor do frete é zero — não por omissão, mas porque
 * é o que a modalidade significa.
 */
/**
 * Situação da cotação (`.spec/010`, com desvio anotado).
 *
 * **A `.spec/010` não pede situação aqui**, e o módulo nasceu sem ela: a cotação
 * não tem saldo a atender nem a baixar, e o que ela tinha era vigência. O desvio
 * é consciente e responde a uma pergunta que a vigência não responde — "esta
 * cotação já virou compra?".
 *
 * São **duas** situações, e não quatro como nos outros documentos. Não há
 * cancelamento nem baixa: a cotação que não deveria existir se exclui enquanto
 * nenhum pedido a tocou, e a que venceu se desativa. O que sobra é a distinção
 * entre a que ainda espera pedido e a que já virou um.
 *
 * **Convive com `ativo`, e não o substitui.** São eixos diferentes: `ativo` é a
 * vigência do preço — a cotação de março pode vencer sem nunca ter virado
 * compra —, e a situação é o consumo. Misturar os dois num campo só faria
 * reativar uma cotação para corrigir um telefone apagar o registro de que houve
 * pedido.
 */
export const SITUACOES_COTACAO = ['ABERTA', 'ENCERRADA'] as const;
export const situacaoCotacaoSchema = z.enum(SITUACOES_COTACAO);
export type SituacaoCotacao = z.infer<typeof situacaoCotacaoSchema>;

export const ROTULO_SITUACAO_COTACAO: Readonly<Record<SituacaoCotacao, string>> = {
  ABERTA: 'Aberta',
  ENCERRADA: 'Encerrada',
};

export const DICA_SITUACAO_COTACAO: Readonly<Record<SituacaoCotacao, string>> = {
  ABERTA: 'Ainda há proposta escolhida esperando virar pedido — ou nenhuma foi escolhida.',
  ENCERRADA: 'Todas as propostas escolhidas viraram pedido de compra. Situação automática.',
};

export const FRETES_COTACAO = ['SEM', 'CIF', 'FOB'] as const;
export const freteCotacaoSchema = z.enum(FRETES_COTACAO);
export type FreteCotacao = z.infer<typeof freteCotacaoSchema>;

export const ROTULO_FRETE_COTACAO: Readonly<Record<FreteCotacao, string>> = {
  SEM: 'Sem frete',
  CIF: 'CIF — por conta do fornecedor',
  FOB: 'FOB — por conta da empresa',
};

export const DICA_FRETE_COTACAO: Readonly<Record<FreteCotacao, string>> = {
  SEM: 'Retirada no balcão, ou serviço prestado no local. Não há transporte a pagar.',
  CIF: 'O fornecedor entrega e cobra pelo transporte. Informe o valor: ele entra no total.',
  FOB: 'O transporte é contratado pela empresa, com outro fornecedor. A cotação não conhece o valor.',
};

/** A modalidade admite valor de frete? Só o `CIF` — ver `FRETES_COTACAO`. */
export function freteTemValor(frete: FreteCotacao): boolean {
  return frete === 'CIF';
}

/**
 * Teto de propostas por cotação.
 *
 * Não é julgamento sobre o negócio: existe para que um `curl` com mil linhas não
 * vire mil `INSERT` numa transação. Uma cotação real ouve entre três e seis
 * fornecedores, e 50 dá folga de uma ordem de grandeza.
 */
export const MAXIMO_PROPOSTAS_COTACAO = 50;

/* -------------------------------------------------------------------------- */
/* Aritmética exata sobre as strings decimais                                 */
/* -------------------------------------------------------------------------- */

/**
 * Os valores derivados são calculados **aqui**, e não no service nem na tela.
 *
 * `valorInsumo` e `valorTotal` saem de contas sobre campos que o usuário digita,
 * e as duas pontas precisam do resultado: a tela mostra o total enquanto ele
 * preenche a proposta, e o servidor grava o número que vale. Duas
 * implementações discordariam em algum arredondamento, e a divergência
 * apareceria como um total que muda ao salvar.
 *
 * As primitivas vivem em `aritmetica.ts`, e não aqui, porque o **pedido de
 * compra** faz as mesmas contas sobre os itens dele. Uma segunda cópia
 * divergiria num arredondamento, e a divergência apareceria como total que muda
 * entre a proposta escolhida e o pedido gerado dela.
 */

/** O que a proposta custa, depois das contas. Strings decimais, como tudo. */
export interface ValoresDaProposta {
  /** `quantidadeOfertada × valorUnitario`, arredondado a centavo. */
  valorInsumo: string;
  /**
   * `valorInsumo + valorOutros + valorFrete − valorDesconto`. É o que se deve ao
   * fornecedor.
   *
   * O frete entra porque, quando existe, é **do mesmo fornecedor e do mesmo
   * pagamento**: só há valor de frete no `CIF`, que é quando ele entrega e cobra
   * por isso. No `FOB` o transporte é outra nota, de outra empresa, e o valor não
   * é conhecido aqui — nesse caso o campo vale zero e a soma não muda.
   */
  valorTotal: string;
  /**
   * `valorTotal + IPI + ICMS ST` — o que a compra **custa de verdade**.
   *
   * É o número pelo qual as propostas se comparam, e não o `valorTotal`. IPI e
   * ICMS ST são cobrados **por fora**: não estão no preço da mercadoria e mesmo
   * assim saem do caixa. Comparar pelo total faria o fornecedor com substituição
   * tributária parecer mais barato do que é — e o de outro estado quase sempre
   * tem ICMS ST.
   *
   * Os demais impostos ficam de fora porque são cobrados **por dentro**: já estão
   * no preço, e somá-los contaria o mesmo dinheiro duas vezes.
   *
   * **Não desconta crédito**, e é a mesma escolha do pedido de compra: o crédito
   * depende da operação fiscal e do regime da empresa, que só existem na nota. Na
   * cotação isto é previsão, e uma previsão que finge saber o crédito erraria
   * mais do que a que assume não saber.
   */
  valorCustoPrevisto: string;
}

export function valoresDaProposta(proposta: {
  quantidadeOfertada: string;
  valorUnitario: string;
  valorOutros: string;
  /** Só existe no frete `CIF`. Zero nas outras modalidades — ver `FRETES_COTACAO`. */
  valorFrete: string;
  valorDesconto: string;
  /**
   * Só os dois que entram na conta. Aceitar o objeto inteiro de impostos daria a
   * impressão de que os outros dez participam de alguma coisa aqui.
   */
  impostos?: { valorIpi: string; valorIcmsSt: string } | undefined;
}): ValoresDaProposta {
  const insumo = multiplicarQuantidadePorValor(proposta.quantidadeOfertada, proposta.valorUnitario);

  const total =
    insumo +
    escalar(proposta.valorOutros, CASAS_DINHEIRO) +
    escalar(proposta.valorFrete, CASAS_DINHEIRO) -
    escalar(proposta.valorDesconto, CASAS_DINHEIRO);

  const porFora =
    proposta.impostos === undefined
      ? 0n
      : escalar(proposta.impostos.valorIpi, CASAS_DINHEIRO) +
        escalar(proposta.impostos.valorIcmsSt, CASAS_DINHEIRO);

  return {
    valorInsumo: desescalar(insumo, CASAS_DINHEIRO),
    valorTotal: desescalar(total, CASAS_DINHEIRO),
    valorCustoPrevisto: desescalar(total + porFora, CASAS_DINHEIRO),
  };
}

/* -------------------------------------------------------------------------- */
/* Desconto: as duas conversões entre percentual e reais                      */
/* -------------------------------------------------------------------------- */

/**
 * A base do desconto é **`quantidade × unitário`** — o `valorInsumo`.
 *
 * Não é o `valorTotal`. Desconto sobre o total incluiria "outros" e o frete, que
 * é justamente o que o fornecedor **não** desconta: quem dá 10% está falando do
 * preço da mercadoria, e a taxa de entrega não entra na conversa. E seria
 * circular — o total já subtrai o desconto que se quer calcular.
 *
 * Escrito em constante porque as duas funções abaixo precisam da mesma base, e
 * uma delas com uma definição diferente da outra faria o par de conversões não
 * fechar: digitar 10%, ver o valor, e o valor devolver 9,7% ao ser relido.
 */
const CEM_PERCENTUAL = 100n * 10n ** BigInt(CASAS_PERCENTUAL);

/**
 * O desconto em reais que corresponde a um percentual da mercadoria.
 *
 * Só existe porque as duas pontas do campo de desconto precisam concordar. A
 * alternativa — a tela fazer a conta com `Number` — erraria centavos: `0.1 * 3`
 * já erra na segunda casa em ponto flutuante, e aqui a segunda casa é o centavo
 * que o fornecedor vai cobrar.
 *
 * Fica no contrato, e não na tela, pelo motivo de `valoresDaProposta`: se um dia
 * a API precisar da mesma conversão — num importador de propostas, numa
 * renegociação em massa —, a conta já está no lugar em que as duas a leem.
 *
 * Arredonda meio para cima, como todo dinheiro do projeto: 10% de R$ 333,33 são
 * R$ 33,33, e não R$ 33,3330 truncado a 33,33 por acaso.
 */
export function descontoPorPercentual(
  quantidadeOfertada: string,
  valorUnitario: string,
  percentualDesconto: string,
): string {
  const base = multiplicarQuantidadePorValor(quantidadeOfertada, valorUnitario);
  const percentual = escalar(percentualDesconto, CASAS_PERCENTUAL);

  return desescalar(dividirArredondando(base * percentual, CEM_PERCENTUAL), CASAS_DINHEIRO);
}

/**
 * O percentual da mercadoria que um desconto em reais representa.
 *
 * **`null` quando a base é zero**, e não `'0'`: sem quantidade ou sem preço
 * unitário não há do que descontar, e um percentual de zero ali afirmaria que o
 * fornecedor não deu desconto — quando o que acontece é que ainda não se sabe.
 * Quem chama decide o que fazer com isso; a tela deixa o campo em branco.
 *
 * **O par não fecha exatamente, e é inerente.** O dinheiro tem duas casas e o
 * percentual tem quatro: 10% de R$ 333,33 são R$ 33,33, e R$ 33,33 sobre a mesma
 * base são 9,9991%. Nenhuma das duas está errada — o que se perde é a fração de
 * centavo que não existe em dinheiro. Por isso a tela **não** reescreve o campo
 * que o usuário acabou de digitar: ela escreve só o outro.
 *
 * O 9,9991 é `dividirArredondando` fazendo o que promete: a conta exata é
 * 9,999099…% e o arredondamento é **meio para cima**. Este comentário já disse
 * 9,9990 — o mesmo número truncado —, e o teste do par foi escrito a partir
 * dele; os dois estavam errados juntos, o que é o modo mais silencioso de um
 * comentário mentir.
 */
export function percentualDoDesconto(
  quantidadeOfertada: string,
  valorUnitario: string,
  valorDesconto: string,
): string | null {
  const base = multiplicarQuantidadePorValor(quantidadeOfertada, valorUnitario);
  if (base === 0n) return null;

  const desconto = escalar(valorDesconto, CASAS_DINHEIRO);

  return desescalar(dividirArredondando(desconto * CEM_PERCENTUAL, base), CASAS_PERCENTUAL);
}

/* -------------------------------------------------------------------------- */
/* Atendimento — o vínculo com a solicitação de compra                        */
/* -------------------------------------------------------------------------- */

/**
 * Quanto desta proposta cobre **qual item de solicitação** (`.spec/010`,
 * "cotacao atendimento").
 *
 * É o vínculo entre a cotação e a solicitação de compra, e ele mora na
 * **proposta**, não no cabeçalho. A razão é a divisão de compra: 100 sacos
 * pedidos pela obra podem sair 60 de um fornecedor e 40 de outro, e é cada
 * oferta que se compromete com uma parte. Um vínculo no cabeçalho da cotação
 * diria "esta cotação é sobre aquela solicitação" e não saberia dizer quanto.
 *
 * **A regra mudou, e o que mudou é o padrão.** Antes o atendimento era opcional
 * mesmo com procedência gravada, e a proposta que nascia sem ele gerava pedido
 * **sem vínculo** — a solicitação ficava parada em `COTACAO` depois de comprada,
 * sem nada acusando. Agora, cotação com procedência sempre atende: o normal é a
 * oferta cobrir tudo o que ela oferta, e a linha de atendimento nasce com a
 * quantidade ofertada. O parcial é a exceção que se declara, e é a única coisa
 * que o formulário pergunta.
 *
 * `solicitacaoCompraItemId` continua **opcional**, e a spec já anota o motivo: a
 * proposta pode atender coisa que não é solicitação — reposição de almoxarifado,
 * compra de escritório. Nesse caso a linha registra só a quantidade
 * comprometida, sem origem.
 *
 * O que **não** existe aqui é a quantidade que o pedido consumiu. Isso é
 * `quantidadePedido`, e mora na proposta: o atendimento é um compromisso da
 * cotação, e o consumo é um fato do pedido. Misturar os dois faria a cotação se
 * declarar consumida por ter sido preenchida.
 */
export const atendimentoCotacaoEntradaSchema = z.object({
  /** Item da solicitação que esta parcela cobre. Nulo em compra sem solicitação. */
  solicitacaoCompraItemId: uuidSchema.nullable().prefault(null),
  quantidadeAtendida: quantidadeSchema.refine(
    (v) => Number(v) > 0,
    'A quantidade atendida deve ser maior que zero.',
  ),
});

export type AtendimentoCotacaoEntrada = z.infer<typeof atendimentoCotacaoEntradaSchema>;

/**
 * Teto de atendimentos por proposta.
 *
 * Mesma natureza dos outros tetos: não é julgamento sobre o negócio, é o que
 * impede um `curl` de virar mil `INSERT` numa transação. Uma proposta que cobre
 * mais de vinte itens de solicitação diferentes é sinal de que a cotação foi
 * montada errada.
 */
export const MAXIMO_ATENDIMENTOS_PROPOSTA = 20;

/**
 * O mesmo item de solicitação duas vezes na mesma proposta é digitação repetida.
 *
 * A soma das duas linhas é o que se queria numa só, e mantê-las separadas faria
 * o relatório de cobertura contar a mesma quantidade duas vezes. As linhas
 * **sem** solicitação passam repetidas: elas não identificam nada, e recusá-las
 * pelo `null` impediria registrar duas parcelas de compra livre.
 */
function validarAtendimentosDistintos(
  atendimentos: AtendimentoCotacaoEntrada[],
  ctx: z.RefinementCtx,
): void {
  const vistos = new Set<string>();

  atendimentos.forEach((atendimento, indice) => {
    const chave = atendimento.solicitacaoCompraItemId;
    if (chave === null) return;

    if (!vistos.has(chave)) {
      vistos.add(chave);
      return;
    }

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [indice, 'solicitacaoCompraItemId'],
      message: 'Este item de solicitação já está atendido nesta proposta. Some as quantidades.',
    });
  });
}

const atendimentosSchema = z
  .array(atendimentoCotacaoEntradaSchema)
  .max(
    MAXIMO_ATENDIMENTOS_PROPOSTA,
    `A proposta atende no máximo ${MAXIMO_ATENDIMENTOS_PROPOSTA} itens de solicitação.`,
  )
  .superRefine(validarAtendimentosDistintos)
  .prefault([]);

/** Atendimento como a API o devolve, com a solicitação de origem identificada. */
export const atendimentoCotacaoSchema = z.object({
  id: uuidSchema,
  solicitacaoCompraItemId: uuidSchema.nullable(),
  /**
   * A solicitação a que o item pertence, desnormalizada.
   *
   * Vem resolvida porque a tela precisa **abrir** a solicitação a partir daqui,
   * e o item sozinho não é endereço de tela nenhuma. Resolver no cliente seria
   * uma consulta por linha.
   */
  solicitacaoCompraId: uuidSchema.nullable(),
  solicitacaoDataLancamento: dataSchema.nullable(),
  obraNome: z.string().nullable(),
  quantidadeAtendida: quantidadeSchema,
});

export type AtendimentoCotacao = z.infer<typeof atendimentoCotacaoSchema>;

/* -------------------------------------------------------------------------- */
/* Propostas                                                                  */
/* -------------------------------------------------------------------------- */

const quantidadeOfertadaSchema = quantidadeSchema.refine(
  (v) => Number(v) > 0,
  'A quantidade ofertada deve ser maior que zero.',
);

/**
 * Valor que não pode ser negativo — o unitário, o de outros e o desconto.
 *
 * Desconto negativo seria acréscimo escondido, e acréscimo tem campo próprio
 * ("outros"). Sem esta recusa, a mesma proposta poderia ser digitada de duas
 * formas que somam igual, e o relatório que separa desconto de despesa acessória
 * passaria a medir a preferência de quem digitou.
 */
const valorNaoNegativoSchema = dinheiroSchema.refine(
  (v) => Number(v) >= 0,
  'O valor não pode ser negativo.',
);

/**
 * O **preço unitário**, que tem seis casas e não duas.
 *
 * Separado do acima porque a escala é outra: o unitário é preço por unidade e
 * aceita `6.123456` — o litro de diesel —, enquanto "outros", frete e desconto
 * são dinheiro do documento e continuam em centavos. Ver `precoUnitarioSchema`.
 */
const precoNaoNegativoSchema = precoUnitarioSchema.refine(
  (v) => Number(v) >= 0,
  'O preço não pode ser negativo.',
);

/* -------------------------------------------------------------------------- */
/* Impostos da proposta                                                       */
/* -------------------------------------------------------------------------- */

/**
 * A tributação que o fornecedor destacou (`.spec/010`, "cotação de preço
 * impostos").
 *
 * São filhos da **proposta**, e não da cotação: cada fornecedor destaca a sua.
 * O de outro estado traz ICMS ST e o daqui não, e é justamente essa diferença
 * que decide de quem sai mais barato comprar.
 *
 * **O desenho não mora mais aqui.** Ele nasceu neste arquivo e mudou-se para
 * `impostos.ts` quando o pedido de compra passou a usar o mesmo — que é o que
 * permitiu apagar a tradução entre os dois formatos que a geração de pedidos
 * carregava. O que sobra aqui são os dois nomes que a cotação usa, apontando
 * para lá: um alias é o jeito de dizer "é o mesmo desenho" sem que nenhum
 * chamador precise mudar de import.
 *
 * A escolha entre os dois conjuntos — material e serviço — é do **documento
 * inteiro** na cotação, e não de cada proposta, porque a cotação tem um insumo
 * só. No pedido a mesma decisão é de cada item. Ver `blocosDeImposto`.
 */
export const impostosDaPropostaEntradaSchema = impostosDestacadosEntradaSchema;
export type ImpostosDaPropostaEntrada = ImpostosDestacadosEntrada;

export const impostosDaPropostaSchema = impostosDestacadosSchema;
export type ImpostosDaProposta = ImpostosDestacados;

/** Impostos todos zerados — a proposta sem tributação destacada. */
export const impostosZeradosDaProposta = impostosZerados;

/**
 * Proposta no payload de gravação.
 *
 * **Não tem `valorInsumo` nem `valorTotal`.** Os dois são conta sobre os outros
 * campos, e aceitá-los deixaria o cliente gravar um total que não bate com o que
 * ele mesmo mandou — a comparação entre fornecedores passaria a medir o que
 * alguém digitou, não o que a proposta custa.
 *
 * **Não tem `quantidadePedido`.** Quem a escreve é o pedido de compra, ao
 * consumir a proposta escolhida. É a mesma decisão de `quantidadeAtendida` no
 * item da solicitação, e pelo mesmo motivo.
 *
 * **Não tem `id`.** As propostas são regravadas por inteiro a cada `PUT`, como
 * as parcelas da condição de pagamento e os itens da solicitação.
 */
export const propostaCotacaoEntradaSchema = z.object({
  fornecedorId: uuidSchema,
  /** Em quantas vezes este fornecedor aceita receber. É parte do preço. */
  condicaoPagamentoId: uuidSchema,
  frete: freteCotacaoSchema.prefault('SEM'),
  /**
   * Quanto o fornecedor se propõe a entregar.
   *
   * Pode diferir da quantidade cotada, e é informação: quem só tem 60 dos 100
   * sacos pedidos entrega menos, e a proposta continua valendo — o comprador é
   * que decide se divide a compra ou procura outro.
   */
  quantidadeOfertada: quantidadeOfertadaSchema,
  /** Preço de uma unidade — **seis casas**, por causa do combustível. */
  valorUnitario: precoNaoNegativoSchema,
  /** Despesas acessórias que o fornecedor cobra à parte — embalagem, montagem. */
  valorOutros: valorNaoNegativoSchema.prefault('0'),
  /**
   * O que o fornecedor cobra pelo transporte. **Só no frete `CIF`.**
   *
   * Campo próprio, e não uma parcela de "outros", porque é o que se negocia
   * separado: "entrego de graça acima de dez mil" é conversa de frete, e diluí-lo
   * em despesas acessórias apagaria a única informação que distingue o
   * fornecedor da esquina do de outro estado.
   *
   * Zero nas demais modalidades, e a recusa é do contrato, da API e do banco —
   * ver `validarFreteDasPropostas` e o `CHECK` do schema. É a mesma repetição
   * deliberada do NCM do insumo: a regra vale por qualquer caminho de escrita.
   */
  valorFrete: valorNaoNegativoSchema.prefault('0'),
  valorDesconto: valorNaoNegativoSchema.prefault('0'),
  /** Até quando o fornecedor sustenta este preço. Opcional. */
  prazoValidade: dataSchema.nullable().prefault(null),
  /**
   * Esta proposta vai virar pedido?
   *
   * **Mais de uma pode ser marcada**, e não é descuido: dividir 100 sacos entre
   * dois fornecedores — 60 de quem tem estoque e 40 de quem entrega mais rápido
   * — é prática corrente. Um `UNIQUE` parcial permitindo só uma escolhida por
   * cotação obrigaria a abrir duas cotações do mesmo material para registrar uma
   * compra que é uma só.
   */
  escolhida: z.boolean().prefault(false),
  /**
   * Que parte desta oferta cobre qual item de solicitação.
   *
   * Nasce vazia, e é o caso comum: o comprador cota primeiro e amarra a
   * cobertura quando decide de quem compra. Exigir ao menos um atendimento
   * obrigaria a inventar uma solicitação para poder registrar o preço que o
   * fornecedor deu por telefone.
   */
  atendimentos: atendimentosSchema,
  /**
   * A tributação destacada pelo fornecedor.
   *
   * Opcional, e o default são doze zeros: a maior parte das cotações não tem
   * imposto destacado nenhum — o fornecedor dá um preço e pronto. Quando tem, é
   * o que separa o barato do que parece barato.
   */
  impostos: impostosDaPropostaEntradaSchema.prefault({}),
  observacao: z.string().trim().max(500, 'A observação tem no máximo 500 caracteres.').optional(),
  /**
   * O link pelo qual o **fornecedor** gravou esta proposta (decisão 117).
   *
   * Não é o `id` que o payload não tem: é a marca de "esta linha é a do
   * fornecedor", devolvida como veio da leitura. Com o link aberto, o service
   * ignora os campos comerciais da linha marcada e mantém os gravados — do
   * comprador só valem `escolhida` e `atendimentos`. A linha travada que não
   * vier no payload também é mantida: o comprador com a tela aberta antes da
   * resposta chegar não a apaga ao salvar.
   *
   * Nula na proposta digitada pelo comprador, que é a maioria.
   */
  conviteCotacaoId: uuidSchema.nullable().prefault(null),
});

export type PropostaCotacaoEntrada = z.infer<typeof propostaCotacaoEntradaSchema>;

/**
 * O mesmo fornecedor com a **mesma condição de pagamento**, duas vezes, é
 * digitação repetida.
 *
 * O par é a chave, e não o fornecedor sozinho: o mesmo fornecedor propondo
 * "à vista com 5%" e "30/60 cheio" são **duas propostas legítimas**, e é
 * justamente entre elas que o comprador escolhe. Recusar pelo fornecedor
 * obrigaria a abrir duas cotações do mesmo material para registrar as duas
 * condições que ele ofereceu na mesma ligação.
 */
function validarPropostasDistintas(
  propostas: PropostaCotacaoEntrada[],
  ctx: z.RefinementCtx,
): void {
  const vistas = new Set<string>();

  propostas.forEach((proposta, indice) => {
    const chave = `${proposta.fornecedorId}·${proposta.condicaoPagamentoId}`;
    if (!vistas.has(chave)) {
      vistas.add(chave);
      return;
    }

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [indice, 'fornecedorId'],
      message:
        'Este fornecedor já propôs nesta condição de pagamento. Para registrar outra oferta dele, troque a condição.',
    });
  });
}

/**
 * A lista de propostas.
 *
 * **Sem `.min(1)`**, ao contrário dos itens da solicitação. A cotação recém-aberta
 * não tem proposta nenhuma — ela é a pergunta, e as respostas chegam depois. É
 * por isso que `prazoResposta` existe.
 */
/**
 * O compromisso não pode ultrapassar a oferta.
 *
 * Uma proposta de 60 sacos não cobre 100 de solicitação, e aceitar isso faria a
 * solicitação aparecer atendida por uma quantidade que ninguém ofereceu. A
 * comparação é sobre inteiros escalados, e não sobre `Number`: dois totais
 * separados por 0,0001 podem empatar em ponto flutuante, e a linha passaria.
 *
 * A recusa chega presa ao índice da **proposta**, e não ao do atendimento: o
 * erro é da soma, e nenhuma linha isolada é a culpada.
 */
function validarCoberturaDasPropostas(
  propostas: PropostaCotacaoEntrada[],
  ctx: z.RefinementCtx,
): void {
  propostas.forEach((proposta, indice) => {
    if (proposta.atendimentos.length === 0) return;

    const comprometido = somarQuantidade(
      proposta.atendimentos.map((atendimento) => atendimento.quantidadeAtendida),
    );
    if (compararQuantidade(comprometido, proposta.quantidadeOfertada) <= 0) return;

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [indice, 'atendimentos'],
      message:
        'A soma do que esta proposta atende passa da quantidade ofertada. Reduza o atendimento ou aumente a oferta.',
    });
  });
}

/**
 * Valor de frete só existe no `CIF`.
 *
 * A recusa é sobre o par frete × valor, e não sobre o campo sozinho — por isso
 * vive aqui e não num `refine` do valor. No `FOB` o transporte é contratado pela
 * construtora, com outro fornecedor e outra nota: um valor digitado ali seria
 * palpite somado ao total como se fosse o que o fornecedor cobrou, e a
 * comparação entre propostas passaria a medir o chute de quem preencheu.
 *
 * A mensagem chega presa ao campo, e não à proposta: é ele que o usuário precisa
 * limpar — ou então trocar a modalidade, que é o outro jeito de resolver, e a
 * frase diz os dois.
 */
function validarFreteDasPropostas(propostas: PropostaCotacaoEntrada[], ctx: z.RefinementCtx): void {
  propostas.forEach((proposta, indice) => {
    if (freteTemValor(proposta.frete) || Number(proposta.valorFrete) === 0) return;

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [indice, 'valorFrete'],
      message:
        'Só o frete CIF tem valor a informar: nele o fornecedor entrega e cobra. Zere o valor, ou mude a modalidade para CIF.',
    });
  });
}

const propostasSchema = z
  .array(propostaCotacaoEntradaSchema)
  .max(MAXIMO_PROPOSTAS_COTACAO, `A cotação tem no máximo ${MAXIMO_PROPOSTAS_COTACAO} propostas.`)
  .superRefine(validarPropostasDistintas)
  .superRefine(validarFreteDasPropostas)
  .superRefine(validarCoberturaDasPropostas);

/** Proposta como a API a devolve, com os nomes e os valores derivados prontos. */
export const propostaCotacaoSchema = z.object({
  id: uuidSchema,
  fornecedorId: uuidSchema,
  /** Nome de uso: fantasia quando há, razão social quando não (`nomeDeUso`). */
  fornecedorNome: z.string().nullable(),
  condicaoPagamentoId: uuidSchema,
  /** `descricao` no cadastro — o nome do campo acompanha a tabela de origem. */
  condicaoPagamentoDescricao: z.string().nullable(),
  frete: freteCotacaoSchema,
  /** Strings decimais, nunca number: ponto flutuante perde casas em silêncio. */
  quantidadeOfertada: quantidadeSchema,
  /** Seis casas: o preço é do fornecedor. O produto dele é que é dinheiro. */
  valorUnitario: precoUnitarioSchema,
  valorInsumo: dinheiroSchema,
  valorOutros: dinheiroSchema,
  /** Cobrado pelo fornecedor no `CIF`. Zero nas outras modalidades. */
  valorFrete: dinheiroSchema,
  valorDesconto: dinheiroSchema,
  valorTotal: dinheiroSchema,
  /** `valorTotal + IPI + ICMS ST`. É por ele que as propostas se comparam. */
  valorCustoPrevisto: dinheiroSchema,
  prazoValidade: dataSchema.nullable(),
  escolhida: z.boolean(),
  /**
   * Quanto desta proposta já virou pedido de compra.
   *
   * Escrita pelo pedido, nunca pelo formulário. É ela que trava a alteração da
   * cotação — o equivalente exato de `quantidadeAtendida` na solicitação.
   */
  quantidadePedido: quantidadeSchema,
  /** O que esta oferta se compromete a cobrir, item de solicitação a item. */
  atendimentos: z.array(atendimentoCotacaoSchema),
  /** A tributação destacada. Vem sempre preenchida, zerada quando não há. */
  impostos: impostosDaPropostaSchema,
  observacao: z.string().nullable(),
  /** O link pelo qual o fornecedor a gravou. Nulo na digitada pelo comprador. */
  conviteCotacaoId: uuidSchema.nullable(),
  /**
   * O link dela ainda está aberto: os campos comerciais são do fornecedor
   * (decisão 117). Calculada na leitura — fecha com o prazo, sem ninguém gravar.
   */
  travadaPeloFornecedor: z.boolean(),
});

export type PropostaCotacao = z.infer<typeof propostaCotacaoSchema>;

/* -------------------------------------------------------------------------- */
/* Documento                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Payload de gravação.
 *
 * `ativo` **entra**, ao contrário da situação da solicitação: aqui não há estado
 * derivado de atendimento nenhum, e desativar a cotação vencida é ação do
 * usuário como em qualquer cadastro (`.spec/010`).
 */
const cotacaoPrecoBaseSchema = z.object({
  /** Empresa do grupo — `pessoas` com `is_empresa`. É ela que define o insumo. */
  empresaId: uuidSchema,
  /** Dia da cotação. Data de negócio, sem hora nem fuso. */
  dataCotacao: dataDoFatoSchema,
  /**
   * O insumo, e **não** a linha de `insumos_empresa` que o banco guarda.
   *
   * Mesma escolha do item da solicitação, e pelo mesmo motivo: pedir o vínculo
   * ao cliente obrigaria a tela a abrir o cadastro de cada insumo para descobrir
   * o id, e o primeiro que errasse o par cotaria material de outra empresa sem
   * que nada acusasse. Quem resolve o par empresa × insumo é o servidor.
   */
  insumoId: uuidSchema,
  quantidadeCotada: quantidadeOfertadaSchema,
  /** Até quando se espera resposta dos fornecedores. Opcional. */
  prazoResposta: dataSchema.nullable().prefault(null),
  /**
   * O item de solicitação de onde esta cotação **nasceu**. Opcional.
   *
   * É procedência, e não compromisso — as duas coisas existem, e são diferentes.
   * A procedência responde "por que esta cotação foi aberta" e é escrita uma vez,
   * na geração a partir da solicitação; o compromisso é `atendimentos`, na
   * proposta, e responde "quanto desta oferta cobre aquele item".
   *
   * Sem esta coluna, a cotação recém-gerada — que nasce **sem proposta**, porque
   * abrir a cotação é o ato de perguntar — não teria onde guardar de onde veio, e
   * o vínculo com a solicitação só apareceria dias depois, quando o primeiro
   * fornecedor respondesse.
   */
  solicitacaoCompraItemId: uuidSchema.nullable().prefault(null),
  propostas: propostasSchema,
  observacao: z.string().trim().max(1000, 'A observação tem no máximo 1000 caracteres.').optional(),
  ativo: z.boolean().prefault(true),
});

/**
 * O payload, com a regra da soma das escolhidas aplicada.
 *
 * A base fica privada e só este nome sai do módulo, como no pedido de compra:
 * dois schemas exportados — um com a regra e outro sem — seriam um convite a
 * importar o errado, e o caminho que escapasse da validação seria justamente o
 * de gravação.
 *
 * **A proposta sozinha acima da cotada passa, e passa de propósito.** A regra
 * aplicada aqui é a da *soma*, e ela só vale a partir da segunda escolhida — ver
 * `somaDasEscolhidasPassaDaCotada`. Uma oferta única de 150 contra 100 cotados é
 * lote fechado, e a API a aceita sem dizer nada.
 *
 * A tela pergunta antes de acrescentá-la — `perguntaDaOferta`, em
 * `SecaoPropostas` —, e a API não pergunta: a confirmação é guarda contra o zero
 * digitado a mais, não regra de negócio, e um `POST` não tem a quem perguntar.
 * Exigir um campo "confirmo" no payload transformaria a pergunta em regra, e o
 * `PUT` de toda cotação de lote fechado já gravada passaria a falhar até a tela
 * remarcar o campo por ela.
 *
 * Travar aqui também não compraria o que parece: o pedido **também** não tem
 * teto contra a proposta — ver `situacaoDaCotacao`, que compara com `>=` porque
 * quem oferta 60 e recebe pedido de 70 negociou isso. A cadeia é sem teto por
 * decisão, e a cotação não é o lugar de inventar um.
 */
export const criarCotacaoPrecoSchema = cotacaoPrecoBaseSchema.superRefine((cotacao, ctx) => {
  const mensagem = erroDaSomaDasEscolhidas(cotacao.propostas, cotacao.quantidadeCotada);
  if (mensagem === null) return;

  ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['propostas'], message: mensagem });
});

export type CriarCotacaoPreco = z.infer<typeof cotacaoPrecoBaseSchema>;
export const atualizarCotacaoPrecoSchema = criarCotacaoPrecoSchema;
export type AtualizarCotacaoPreco = CriarCotacaoPreco;

/**
 * Cotação como a API a devolve.
 *
 * Os nomes de empresa e insumo vêm **desnormalizados**, e a unidade junto: a
 * listagem mostra "Cimento CP-II · 100 SC" e não dois uuid, e resolver isso no
 * cliente significaria duas consultas por linha.
 */
export const cotacaoPrecoSchema = z.object({
  id: uuidSchema,
  empresaId: uuidSchema,
  empresaNome: z.string().nullable(),
  dataCotacao: dataSchema,
  /** O vínculo gravado. Vem na leitura porque o pedido vai apontar para ele. */
  insumoEmpresaId: uuidSchema,
  /** O insumo por trás do vínculo — é ele que o formulário devolve no `PUT`. */
  insumoId: uuidSchema,
  insumoNome: z.string().nullable(),
  unidadeSigla: z.string().nullable(),
  quantidadeCotada: quantidadeSchema,
  prazoResposta: dataSchema.nullable(),
  /** O item de solicitação de onde a cotação nasceu. Nula na cotação avulsa. */
  solicitacaoCompraItemId: uuidSchema.nullable(),
  /** A solicitação daquele item, resolvida — é ela que a tela sabe abrir. */
  solicitacaoCompraId: uuidSchema.nullable(),
  /**
   * Data e obra da solicitação de origem, desnormalizadas.
   *
   * Existem pelo mesmo motivo que as gêmeas de `atendimentoCotacaoSchema`: a
   * seção de origem da tela escreve "Solicitação de 12/04 · Residencial Aurora",
   * e um uuid solto não diz de qual documento se fala. Resolvê-las no cliente
   * seria uma consulta por cotação só para desenhar um cabeçalho.
   */
  solicitacaoDataLancamento: dataSchema.nullable(),
  solicitacaoObraNome: z.string().nullable(),
  /**
   * `ABERTA` ou `ENCERRADA`, derivada do consumo das propostas.
   *
   * Não entra no payload de gravação: quem a escreve é o pedido de compra, ao
   * consumir a proposta escolhida. Aceitá-la deixaria marcar como encerrada uma
   * cotação que nenhum pedido tocou.
   */
  situacao: situacaoCotacaoSchema,
  observacao: z.string().nullable(),
  ativo: z.boolean(),
  /**
   * As propostas vêm **na listagem também**, não só no documento aberto.
   *
   * É o que distingue duas cotações do mesmo insumo na mesma semana, e é o que a
   * coluna "Propostas" resume. O custo é uma segunda consulta por página, não
   * uma por linha — a mesma escolha das parcelas da condição de pagamento.
   */
  propostas: z.array(propostaCotacaoSchema),
  createdAt: dataHoraSchema,
  updatedAt: dataHoraSchema,
});

export type CotacaoPreco = z.infer<typeof cotacaoPrecoSchema>;

export const listaCotacoesPrecoSchema = respostaListaSchema(cotacaoPrecoSchema);
export type ListaCotacoesPreco = z.infer<typeof listaCotacoesPrecoSchema>;

/* -------------------------------------------------------------------------- */
/* Listagem                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Filtros da listagem.
 *
 * Tem `ativo`, e é o que separa esta tela da de solicitações: lá o recorte
 * natural é a situação, aqui é a vigência. O filtro de **fornecedor** olha as
 * propostas, e não o mestre — "quanto a Casa do Construtor cobrou este ano" é a
 * pergunta que o comprador faz antes de ligar para ela de novo.
 *
 * **Sem busca livre.** Todo recorte desta tela é por id — insumo, fornecedor — ou
 * por valor fechado — vigência, situação, período. Não sobrou nada que texto
 * solto responda melhor: a observação da cotação é anotação de quem digitou, e
 * procurar por ela devolvia acertos que o comprador não pediu.
 */
export const listarCotacoesPrecoQuerySchema = paginacaoSchema.omit({ q: true }).extend({
  empresaId: uuidSchema.optional(),
  /**
   * O material cotado, por id.
   *
   * Substituiu a busca livre que a tela tinha. O `q` saiu do schema — e não
   * ficou aceito e ignorado — porque parâmetro que a API recebe e descarta é
   * pior do que parâmetro que ela recusa: quem chamasse com `q` acharia que
   * filtrou.
   *
   * A troca é ganho de precisão: o texto livre varria observação e nome do
   * insumo com `ILIKE`, então "cabo" trazia junto a cotação cuja observação
   * mencionava cabo. O seletor manda o id, e o id é exato — a busca por nome
   * acontece dentro dele, no cadastro de insumos, com o `tsvector` em português.
   */
  insumoId: uuidSchema.optional(),
  /** Recorta pelas propostas: traz a cotação em que este fornecedor apareceu. */
  fornecedorId: uuidSchema.optional(),
  /**
   * As cotações ligadas a uma solicitação — pela procedência **ou** pelo
   * atendimento.
   *
   * Os dois caminhos entram no mesmo filtro de propósito: quem pergunta "o que
   * já foi cotado desta solicitação" não distingue a cotação que nasceu dela da
   * que se comprometeu com ela depois. Separar em dois filtros obrigaria a olhar
   * duas listas para responder uma pergunta.
   */
  solicitacaoCompraId: uuidSchema.optional(),
  /**
   * Recorta pelo consumo, e não pela vigência.
   *
   * "O que ainda não virou pedido" é a pergunta do comprador que vai fechar
   * compra, e ela é diferente de "o que ainda vale" — a cotação pode estar
   * vigente e já encerrada, ou vencida sem nunca ter virado nada.
   */
  situacao: situacaoCotacaoSchema.optional(),
  ativo: filtroAtivoSchema,
  /** Início do período, inclusivo. */
  de: dataSchema.optional(),
  /** Fim do período, inclusivo. */
  ate: dataSchema.optional(),
});

export type ListarCotacoesPrecoQuery = z.infer<typeof listarCotacoesPrecoQuerySchema>;

export const COLUNAS_ORDENAVEIS_COTACAO_PRECO = ['dataCotacao', 'createdAt'] as const;

/* -------------------------------------------------------------------------- */
/* Geração de pedidos a partir das propostas escolhidas                       */
/* -------------------------------------------------------------------------- */

/**
 * As propostas que esperam virar pedido.
 *
 * A tela que consome isto é a de **geração de pedidos**, e ela existe porque a
 * pergunta do comprador não cabe na listagem de cotações: "o que eu já decidi
 * comprar e ainda não pedi" atravessa documentos, e responder pela tela de
 * cotação exigiria abrir uma a uma para ver quais têm proposta marcada.
 *
 * A unidade é a **proposta**, e não a cotação, porque é ela que carrega o que o
 * pedido precisa: fornecedor, condição de pagamento, preço e quantidade. Uma
 * cotação pode ter duas propostas escolhidas — a compra dividida entre dois
 * fornecedores é prática corrente —, e nesse caso são duas linhas, que viram
 * dois pedidos diferentes.
 *
 * Os quatro filtros são os que o comprador usa para fechar compra: a empresa,
 * porque o pedido é de uma só; o grupo e o insumo, porque se compra por
 * família de material; e o fornecedor, porque a ligação é para ele.
 */
export const listarPropostasAPedirQuerySchema = paginacaoSchema.extend({
  empresaId: uuidSchema.optional(),
  /** Recorta o **ramo inteiro** do grupo, como o filtro da listagem de insumos. */
  grupoInsumoId: uuidSchema.optional(),
  insumoId: uuidSchema.optional(),
  fornecedorId: uuidSchema.optional(),
});

export type ListarPropostasAPedirQuery = z.infer<typeof listarPropostasAPedirQuerySchema>;

/**
 * Uma proposta escolhida, com o que a tela precisa para decidir e agrupar.
 *
 * Traz o **fornecedor e a condição de pagamento** em destaque porque são a chave
 * do agrupamento: quem seleciona cinco linhas do mesmo fornecedor gera um pedido
 * só, e a tela precisa poder mostrar isso antes de gerar.
 */
export const propostaAPedirSchema = z.object({
  /** A **proposta**. É ela que o pedido consome e que a seleção manda de volta. */
  id: uuidSchema,
  cotacaoPrecoId: uuidSchema,
  dataCotacao: dataSchema,

  empresaId: uuidSchema,
  empresaNome: z.string().nullable(),

  insumoId: uuidSchema,
  insumoNome: z.string().nullable(),
  grupoNome: z.string().nullable(),
  unidadeSigla: z.string().nullable(),
  /**
   * Casas do preço daquele insumo, desnormalizadas como a sigla.
   *
   * A listagem mostra o unitário de cada proposta, e sem isto ela o mostraria
   * com duas casas em todas — o litro de diesel a `6,123456` apareceria `6,12`
   * numa tela cuja função é comparar preços. Vem da junção que já traz o nome e
   * a unidade; não custa consulta nova.
   */
  casasDecimaisPreco: z.number().int(),

  fornecedorId: uuidSchema,
  /** Nome de uso: fantasia quando há, razão social quando não (`nomeDeUso`). */
  fornecedorNome: z.string().nullable(),
  condicaoPagamentoId: uuidSchema,
  condicaoPagamentoDescricao: z.string().nullable(),

  frete: freteCotacaoSchema,
  valorFrete: dinheiroSchema,

  quantidadeOfertada: quantidadeSchema,
  /** Quanto desta proposta já virou pedido. Escrita pelo pedido, nunca aqui. */
  quantidadePedido: quantidadeSchema,
  /** `ofertada − pedido`. É a quantidade que o pedido gerado vai carregar. */
  quantidadeAPedir: quantidadeSchema,

  /** Seis casas — é o preço, e é ele que o pedido gerado copia. */
  valorUnitario: precoUnitarioSchema,
  valorTotal: dinheiroSchema,
  /** `valorTotal + IPI + ICMS ST`. É por ele que a proposta foi escolhida. */
  valorCustoPrevisto: dinheiroSchema,

  /**
   * A solicitação que esta proposta cobre, quando cobre uma só.
   *
   * Nula na proposta avulsa **e** na que se comprometeu com mais de um item —
   * nesse caso não há uma solicitação a nomear, e inventar a primeira daria a
   * impressão de que o pedido vai baixar só aquela. Ver `MOTIVO_SEM_VINCULO`.
   */
  solicitacaoCompraId: uuidSchema.nullable(),
  solicitacaoCompraItemId: uuidSchema.nullable(),
  solicitacaoDataLancamento: dataSchema.nullable(),
  obraNome: z.string().nullable(),
  /** Quantos itens de solicitação esta proposta cobre. Zero na avulsa. */
  atendimentos: z.number().int(),
});

export type PropostaAPedir = z.infer<typeof propostaAPedirSchema>;

export const listaPropostasAPedirSchema = respostaListaSchema(propostaAPedirSchema);
export type ListaPropostasAPedir = z.infer<typeof listaPropostasAPedirSchema>;

/**
 * Teto de propostas por geração.
 *
 * Mesma natureza dos outros tetos: impede que um `curl` vire centenas de
 * documentos numa transação. Cem propostas já é uma leva de compra grande.
 *
 * Declarada **antes** do schema que a usa: `const` não é içada, e uma referência
 * de baixo para cima aqui quebraria na carga do módulo, não na chamada.
 */
export const MAXIMO_PROPOSTAS_A_PEDIR = 100;

/**
 * Gerar os pedidos das propostas selecionadas.
 *
 * A seleção é de propostas, e o resultado é **um pedido por fornecedor**: o
 * agrupamento acontece no servidor, e não na tela, porque é ele quem sabe a
 * condição de pagamento e o frete de cada linha — e porque duas telas
 * agrupando pelo mesmo critério divergiriam na primeira correção.
 */
export const gerarPedidosDeCotacoesSchema = z.object({
  propostaIds: z
    .array(uuidSchema)
    .min(1, 'Escolha ao menos uma proposta.')
    .max(MAXIMO_PROPOSTAS_A_PEDIR, `Selecione no máximo ${MAXIMO_PROPOSTAS_A_PEDIR} propostas.`),
  /**
   * Dia dos pedidos gerados. Ausente vale **hoje**, resolvido no servidor.
   *
   * Não é a data da cotação: o preço foi perguntado em abril, e o compromisso
   * está sendo assumido hoje. Copiar a data de origem produziria pedido nascido
   * com data anterior à decisão de comprar.
   */
  dataLancamento: dataSchema.optional(),
});

export type GerarPedidosDeCotacoes = z.infer<typeof gerarPedidosDeCotacoesSchema>;

/** O que a geração fez, para a tela dizer sem reler nada. */
export const pedidosGeradosSchema = z.object({
  /** Ids dos pedidos criados — um por fornecedor × condição × frete. */
  pedidoIds: z.array(uuidSchema),
  /**
   * Propostas **selecionadas** que já não tinham saldo a pedir.
   *
   * Cresce quando outro usuário gerou o pedido daquela linha entre a abertura da
   * tela e a confirmação. O número normal é zero, e por isso ele é aviso e não
   * erro: a leva continua valendo para o resto.
   */
  propostasSemSaldo: z.number().int(),
});

export type PedidosGerados = z.infer<typeof pedidosGeradosSchema>;

/**
 * A proposta vira item de pedido ligado a uma solicitação?
 *
 * Só quando ela cobre **exatamente um** item. Com nenhum, é compra avulsa e não
 * há o que ligar; com dois ou mais, o item do pedido não daria conta — ele
 * aponta para uma solicitação só, e escolher uma das duas faria o pedido baixar
 * o saldo de uma e deixar a outra pendente sem explicação.
 *
 * O caso de dois ou mais é raro a ponto de quase não existir: o formulário da
 * cotação oferece um atendimento por proposta, e só uma chamada direta à API
 * cria mais. A tela avisa quando encontra um.
 */
export function propostaGeraVinculoDeSolicitacao(proposta: { atendimentos: number }): boolean {
  return proposta.atendimentos === 1;
}

/* -------------------------------------------------------------------------- */
/* Regras compartilhadas entre a tela e a API                                 */
/* -------------------------------------------------------------------------- */

/**
 * A situação da cotação, a partir do consumo das propostas.
 *
 * `ENCERRADA` quando há proposta escolhida **e todas elas** já viraram pedido.
 * As duas metades importam:
 *
 * - **"há escolhida"** porque a cotação sem nenhuma decisão não está encerrada,
 *   está esperando. Sem essa metade, toda cotação recém-aberta nasceria
 *   encerrada — "todas as zero escolhidas viraram pedido" é verdade vazia.
 * - **"todas"** por causa da compra dividida: 100 sacos comprados 60 de um
 *   fornecedor e 40 de outro são duas propostas escolhidas na mesma cotação, e
 *   encerrar no primeiro pedido tiraria a segunda da tela de geração — a metade
 *   restante da compra ficaria inalcançável.
 *
 * A comparação é `quantidadePedido >= quantidadeOfertada`, e o `>=` não é
 * descuido: a proposta não tem teto: quem oferta 60 e recebe pedido de 70 é um
 * caso que o comprador negociou, e a cotação registra o que foi consumido.
 *
 * Vive no contrato porque as duas pontas precisam da **mesma** regra: o servidor
 * grava a coluna e a tela precisa saber o que ela significa.
 */
export function situacaoDaCotacao(
  propostas: readonly {
    escolhida: boolean;
    quantidadePedido: string;
    quantidadeOfertada: string;
  }[],
): SituacaoCotacao {
  const escolhidas = propostas.filter((proposta) => proposta.escolhida);
  if (escolhidas.length === 0) return 'ABERTA';

  const todasViraramPedido = escolhidas.every(
    (proposta) => compararQuantidade(proposta.quantidadePedido, proposta.quantidadeOfertada) >= 0,
  );

  return todasViraramPedido ? 'ENCERRADA' : 'ABERTA';
}

/**
 * Quanto as propostas **escolhidas** somam.
 *
 * Só as escolhidas, e pela quantidade **ofertada**: escolher é dizer "vou
 * comprar isto deste fornecedor", e o que se vai comprar é o que ele ofertou. As
 * não escolhidas são preço perguntado e não comprado — somá-las mediria de
 * quantos fornecedores se pediu orçamento.
 */
export function somaDasEscolhidas(
  propostas: readonly { escolhida: boolean; quantidadeOfertada: string }[],
): string {
  return somarQuantidade(
    propostas.filter((proposta) => proposta.escolhida).map((p) => p.quantidadeOfertada),
  );
}

/**
 * A soma das escolhidas passa da quantidade cotada? A mensagem, ou `null`.
 *
 * A regra existe para pegar a **compra dividida que passa da conta**: 100 sacos
 * cotados, 60 marcados de um fornecedor e 60 de outro, e o comprador gera dois
 * pedidos somando 120 sem que nada tenha acusado. É um erro caro, porque só
 * aparece quando o material chega ao canteiro.
 *
 * **Vale a partir da segunda escolhida**, e a exceção é deliberada. Uma proposta
 * sozinha acima da cotada é o lote fechado — o fornecedor que só vende caixa
 * cheia, o preço melhor na quantidade maior —, e a tela já trata esse caso
 * perguntando antes de a proposta entrar na lista (`ofertaPassaDaCotada`, no
 * formulário). Recusá-la aqui tornaria impossível registrar uma compra legítima
 * sem antes mexer na quantidade do cabeçalho, que é o número que a obra pediu.
 *
 * O que a exceção **não** abre é a divisão: assim que há duas escolhidas, a soma
 * é teto. Duas ofertas de lote fechado somando o dobro do necessário não é
 * negociação, é seleção errada.
 *
 * Vive no contrato porque as duas pontas precisam da mesma regra: a tela avisa
 * enquanto o comprador marca, e a API recusa o `PUT`. Duas cópias divergiriam na
 * primeira correção.
 */
export function somaDasEscolhidasPassaDaCotada(
  propostas: readonly { escolhida: boolean; quantidadeOfertada: string }[],
  quantidadeCotada: string,
): boolean {
  const escolhidas = propostas.filter((proposta) => proposta.escolhida);
  if (escolhidas.length < 2) return false;

  // Inteiros escalados, e não `Number`: duas quantidades separadas por 0,0001
  // empatariam em ponto flutuante, e a linha passaria.
  return compararQuantidade(somaDasEscolhidas(escolhidas), quantidadeCotada) > 0;
}

/**
 * A mesma regra, com a mensagem que a **API** devolve.
 *
 * Duas funções, e não uma, porque as duas pontas apresentam o mesmo fato de
 * formas diferentes: a tela escreve os números **na unidade do insumo** — "120
 * SC contra 100 SC" é a conferência que o comprador faz —, e o contrato não
 * conhece a sigla. O que não se duplica é a decisão, que é
 * `somaDasEscolhidasPassaDaCotada`.
 */
export function erroDaSomaDasEscolhidas(
  propostas: readonly { escolhida: boolean; quantidadeOfertada: string }[],
  quantidadeCotada: string,
): string | null {
  if (!somaDasEscolhidasPassaDaCotada(propostas, quantidadeCotada)) return null;

  return `As propostas escolhidas somam ${somaDasEscolhidas(propostas)} e a cotação pede ${quantidadeCotada}. Desmarque uma, ou reduza a quantidade ofertada de alguma delas.`;
}

/**
 * A cotação ainda se altera?
 *
 * Vive no contrato, e não no formulário nem no service, porque os dois precisam
 * da **mesma** regra: a tela decide se os campos são editáveis, e a API decide
 * se aceita o `PUT`. Duas cópias divergiriam na primeira correção, e a
 * divergência apareceria como formulário que deixa digitar e servidor que
 * recusa.
 *
 * A trava é o pedido, e **não o `ativo`**: cotação desativada continua editável,
 * porque desativar é dizer "este preço venceu", não "este documento está
 * fechado". Já a proposta que virou pedido não se mexe — regravar as propostas
 * apagaria a quantidade que o pedido consumiu.
 */
export function cotacaoEditavel(propostas: readonly { quantidadePedido: string }[]): boolean {
  return propostas.every((proposta) => Number(proposta.quantidadePedido) <= 0);
}

/**
 * O que se está cotando ainda se altera?
 *
 * Responde por **três campos** do cabeçalho — empresa, insumo e quantidade
 * cotada —, e não pelo documento inteiro: data, prazo de resposta, observação e
 * `ativo` continuam livres, e as propostas também. A trava aqui não é sobre
 * quando a cotação foi feita, é sobre **o que ela pergunta**.
 *
 * Falsa assim que existe procedência, porque os três vêm da solicitação: a
 * empresa é a do documento de origem, o insumo é o do item, e a quantidade é o
 * saldo em aberto daquele item na hora em que a cotação foi aberta. Alterá-los
 * faria a cotação responder uma pergunta que ninguém fez — e a seção de origem
 * continuaria apontando para um item de outro material, ou de outra empresa.
 *
 * A cotação **avulsa** não tem essa amarra: ela não responde a documento nenhum,
 * e corrigir o insumo que se digitou errado é o caso normal de uso dela.
 *
 * Vive no contrato pelo mesmo motivo que `cotacaoEditavel`: a tela decide se
 * desabilita os campos, e a API decide se aceita o `PUT`. Duas cópias
 * divergiriam na primeira correção, e a divergência apareceria como formulário
 * que deixa digitar e servidor que recusa.
 */
export function objetoDaCotacaoEditavel(cotacao: {
  solicitacaoCompraItemId: string | null;
}): boolean {
  return cotacao.solicitacaoCompraItemId === null;
}

/**
 * A proposta mais barata, pelo **custo previsto**.
 *
 * Três decisões empilhadas, e cada uma corrige um jeito de errar:
 *
 * 1. **Custo, e não total.** IPI e ICMS ST são cobrados por fora e saem do
 *    caixa. Comparar pelo total faria o fornecedor de outro estado — que quase
 *    sempre tem ICMS ST — parecer mais barato do que é.
 * 2. **Total, e não unitário.** Quem cobra menos por saco e cobra a entrega à
 *    parte pode sair mais caro.
 * 3. **Inteiros escalados, e não `Number`.** Dois custos que diferem por um
 *    centavo em valores altos empatariam em ponto flutuante, e o destaque de
 *    "menor preço" cairia na linha errada.
 *
 * Empate devolve a primeira, que é a de menor id — ordem de gravação, estável
 * entre chamadas. Devolve `undefined` na cotação sem proposta, que é como ela
 * nasce.
 */
export function melhorProposta<T extends { valorCustoPrevisto: string }>(
  propostas: readonly T[],
): T | undefined {
  let melhor: T | undefined;
  let menor = 0n;

  for (const proposta of propostas) {
    const custo = escalar(proposta.valorCustoPrevisto, CASAS_DINHEIRO);
    if (melhor === undefined || custo < menor) {
      melhor = proposta;
      menor = custo;
    }
  }

  return melhor;
}

/* -------------------------------------------------------------------------- */
/* De que solicitações esta cotação vem                                       */
/* -------------------------------------------------------------------------- */

/** Uma solicitação que alcança esta cotação, por um caminho ou pelos dois. */
export interface SolicitacaoDeOrigem {
  solicitacaoCompraId: string;
  solicitacaoCompraItemId: string;
  dataLancamento: string | null;
  obraNome: string | null;
  /**
   * É a **procedência** — o item que fez a cotação existir.
   *
   * Distinta do compromisso de propósito: a cotação gerada de uma solicitação
   * nasce com procedência e **sem** atendimento nenhum, porque abrir a cotação é
   * o ato de perguntar. Mostrar as duas com o mesmo rótulo faria a cotação
   * recém-gerada parecer já comprometida com um saldo que ninguém ofereceu.
   */
  procedencia: boolean;
  /** Soma do que as propostas se comprometeram a cobrir daquele item. `'0'` quando nenhuma. */
  quantidadeComprometida: string;
}

/**
 * De que solicitações esta cotação vem — os dois caminhos, numa lista só.
 *
 * Vive no contrato pelo mesmo motivo que `resumoDePropostas`: a seção de origem
 * do formulário e a listagem respondem a mesma pergunta, e duas implementações
 * discordariam sobre a cotação que tem procedência de um item e atendimento de
 * outro — que é legítima, e é justamente o caso que ninguém lembra de tratar.
 *
 * A consolidação é **por item de solicitação**, e não por solicitação: duas
 * linhas do mesmo documento pedindo o mesmo material para depósitos diferentes
 * são dois saldos distintos, e somá-las esconderia qual deles a proposta cobre.
 *
 * A ordem põe a procedência primeiro — é a que explica por que a cotação existe
 * — e o resto na ordem em que os atendimentos foram gravados, que é estável
 * porque o id é uuidv7.
 */
export function solicitacoesDeOrigem(cotacao: {
  solicitacaoCompraItemId: string | null;
  solicitacaoCompraId: string | null;
  solicitacaoDataLancamento: string | null;
  solicitacaoObraNome: string | null;
  propostas: readonly { atendimentos: readonly AtendimentoCotacao[] }[];
}): SolicitacaoDeOrigem[] {
  const porItem = new Map<string, SolicitacaoDeOrigem>();

  if (cotacao.solicitacaoCompraItemId !== null && cotacao.solicitacaoCompraId !== null) {
    porItem.set(cotacao.solicitacaoCompraItemId, {
      solicitacaoCompraId: cotacao.solicitacaoCompraId,
      solicitacaoCompraItemId: cotacao.solicitacaoCompraItemId,
      dataLancamento: cotacao.solicitacaoDataLancamento,
      obraNome: cotacao.solicitacaoObraNome,
      procedencia: true,
      quantidadeComprometida: '0',
    });
  }

  for (const proposta of cotacao.propostas) {
    for (const atendimento of proposta.atendimentos) {
      const { solicitacaoCompraItemId: itemId, solicitacaoCompraId: documentoId } = atendimento;
      // Atendimento sem item é compra livre — reposição de almoxarifado, material
      // de escritório. Ele não vem de solicitação nenhuma, e listá-lo aqui
      // inventaria uma origem que não existe.
      if (itemId === null || documentoId === null) continue;

      const atual = porItem.get(itemId);

      porItem.set(itemId, {
        solicitacaoCompraId: documentoId,
        solicitacaoCompraItemId: itemId,
        dataLancamento: atendimento.solicitacaoDataLancamento,
        obraNome: atendimento.obraNome,
        procedencia: atual?.procedencia ?? false,
        quantidadeComprometida: somarQuantidade([
          atual?.quantidadeComprometida ?? '0',
          atendimento.quantidadeAtendida,
        ]),
      });
    }
  }

  return [...porItem.values()].sort((a, b) => Number(b.procedencia) - Number(a.procedencia));
}

/**
 * Resumo das propostas, para a listagem: `4 propostas · 2 escolhidas`.
 *
 * Vive no contrato pelo mesmo motivo que `resumoDeItens`: a listagem e o seletor
 * de cotação do pedido de compra mostram a mesma coisa, e duas implementações
 * escreveriam "0 escolhidas" e "nenhuma escolhida" para o mesmo documento.
 */
export function resumoDePropostas(propostas: readonly PropostaCotacao[]): string {
  if (propostas.length === 0) return 'Aguardando resposta';

  const plural = propostas.length === 1 ? 'proposta' : 'propostas';
  const escolhidas = propostas.filter((proposta) => proposta.escolhida).length;

  if (escolhidas === 0) return `${propostas.length} ${plural}`;
  return `${propostas.length} ${plural} · ${escolhidas} escolhida${escolhidas === 1 ? '' : 's'}`;
}
